import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Plus, Edit, Trash2, X, Save } from 'lucide-react';

export default function ManagePlanDetails() {
  const [planInfo, setPlanInfo] = useState(null);
  const [ictTeamMembers, setIctTeamMembers] = useState([]);
  const [masterplanners, setMasterplanners] = useState([]);
  const [projectLeads, setProjectLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingIctMember, setEditingIctMember] = useState(null);
  const [editingMasterplanner, setEditingMasterplanner] = useState(null);
  const [editingProjectLead, setEditingProjectLead] = useState(null);
  
  const [planForm, setPlanForm] = useState({
    detailedInfo: ''
  });

  const [ictMemberForm, setIctMemberForm] = useState({
    name: '',
    imageUrl: ''
  });

  const [masterplannerForm, setMasterplannerForm] = useState({
    name: '',
    linkedinUrl: ''
  });

  const [projectLeadForm, setProjectLeadForm] = useState({
    name: '',
    linkedinUrl: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch plan info
      const planSnapshot = await getDocs(collection(db, 'planInfo'));
      if (planSnapshot.docs.length > 0) {
        const data = planSnapshot.docs[0];
        setPlanInfo({ id: data.id, ...data.data() });
        setPlanForm(data.data());
      }

      // Fetch ICT team members and sort by creation date (oldest first)
      const ictMembersSnapshot = await getDocs(collection(db, 'ictTeamMembers'));
      const ictMembersData = ictMembersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort by createdAt timestamp, oldest first
      ictMembersData.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return aTime - bTime; // Ascending order (oldest first)
      });
      setIctTeamMembers(ictMembersData);

      // Fetch masterplanners
      const masterplannersSnapshot = await getDocs(collection(db, 'masterplanners'));
      const masterplannersData = masterplannersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      masterplannersData.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return aTime - bTime;
      });
      setMasterplanners(masterplannersData);

      // Fetch project leads
      const projectLeadsSnapshot = await getDocs(collection(db, 'projectLeads'));
      const projectLeadsData = projectLeadsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      projectLeadsData.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return aTime - bTime;
      });
      setProjectLeads(projectLeadsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlanInfo = async (e) => {
    e.preventDefault();
    
    try {
      if (planInfo) {
        await updateDoc(doc(db, 'planInfo', planInfo.id), {
          detailedInfo: planForm.detailedInfo,
          updatedAt: serverTimestamp()
        });
        alert('Plan details updated successfully!');
      } else {
        const docRef = await addDoc(collection(db, 'planInfo'), {
          detailedInfo: planForm.detailedInfo,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setPlanInfo({ id: docRef.id, detailedInfo: planForm.detailedInfo });
        alert('Plan details created successfully!');
      }
      fetchData();
    } catch (err) {
      console.error('Error saving plan info:', err);
      alert('Error saving plan details');
    }
  };

  // ICT Team Member functions
  const handleSaveIctMember = async (e) => {
    e.preventDefault();
    
    if (!ictMemberForm.name) {
      alert('Please fill in the name field');
      return;
    }
    
    try {
      if (editingIctMember) {
        await updateDoc(doc(db, 'ictTeamMembers', editingIctMember.id), {
          name: ictMemberForm.name,
          imageUrl: ictMemberForm.imageUrl || '',
          updatedAt: serverTimestamp()
        });
        alert('ICT team member updated successfully!');
        setEditingIctMember(null);
      } else {
        await addDoc(collection(db, 'ictTeamMembers'), {
          name: ictMemberForm.name,
          imageUrl: ictMemberForm.imageUrl || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        alert('ICT team member added successfully!');
      }
      setIctMemberForm({ name: '', imageUrl: '' });
      fetchData();
    } catch (err) {
      console.error('Error saving ICT team member:', err);
      alert('Error saving ICT team member: ' + err.message);
    }
  };

  const handleEditIctMember = (member) => {
    setEditingIctMember(member);
    setIctMemberForm({
      name: member.name,
      imageUrl: member.imageUrl
    });
  };

  const handleDeleteIctMember = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ICT team member?')) return;
    try {
      await deleteDoc(doc(db, 'ictTeamMembers', id));
      alert('ICT team member deleted successfully!');
      fetchData();
    } catch (err) {
      console.error('Error deleting ICT team member:', err);
      alert('Error deleting ICT team member');
    }
  };

  const handleCancelIctEdit = () => {
    setEditingIctMember(null);
    setIctMemberForm({ name: '', imageUrl: '' });
  };

  // Masterplanner functions
  const handleSaveMasterplanner = async (e) => {
    e.preventDefault();
    if (!masterplannerForm.name) {
      alert('Please fill in the name field');
      return;
    }
    try {
      if (editingMasterplanner) {
        await updateDoc(doc(db, 'masterplanners', editingMasterplanner.id), {
          name: masterplannerForm.name,
          linkedinUrl: masterplannerForm.linkedinUrl || '',
          updatedAt: serverTimestamp()
        });
        alert('Masterplanner updated successfully!');
        setEditingMasterplanner(null);
      } else {
        await addDoc(collection(db, 'masterplanners'), {
          name: masterplannerForm.name,
          linkedinUrl: masterplannerForm.linkedinUrl || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        alert('Masterplanner added successfully!');
      }
      setMasterplannerForm({ name: '', linkedinUrl: '' });
      fetchData();
    } catch (err) {
      console.error('Error saving masterplanner:', err);
      alert('Error saving masterplanner: ' + err.message);
    }
  };

  const handleEditMasterplanner = (person) => {
    setEditingMasterplanner(person);
    setMasterplannerForm({
      name: person.name,
      linkedinUrl: person.linkedinUrl
    });
  };

  const handleDeleteMasterplanner = async (id) => {
    if (!window.confirm('Are you sure you want to delete this masterplanner?')) return;
    try {
      await deleteDoc(doc(db, 'masterplanners', id));
      alert('Masterplanner deleted successfully!');
      fetchData();
    } catch (err) {
      console.error('Error deleting masterplanner:', err);
      alert('Error deleting masterplanner');
    }
  };

  const handleCancelMasterplannerEdit = () => {
    setEditingMasterplanner(null);
    setMasterplannerForm({ name: '', linkedinUrl: '' });
  };

  // Project Lead functions
  const handleSaveProjectLead = async (e) => {
    e.preventDefault();
    if (!projectLeadForm.name) {
      alert('Please fill in the name field');
      return;
    }
    try {
      if (editingProjectLead) {
        await updateDoc(doc(db, 'projectLeads', editingProjectLead.id), {
          name: projectLeadForm.name,
          linkedinUrl: projectLeadForm.linkedinUrl || '',
          updatedAt: serverTimestamp()
        });
        alert('Project Lead updated successfully!');
        setEditingProjectLead(null);
      } else {
        await addDoc(collection(db, 'projectLeads'), {
          name: projectLeadForm.name,
          linkedinUrl: projectLeadForm.linkedinUrl || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        alert('Project Lead added successfully!');
      }
      setProjectLeadForm({ name: '', linkedinUrl: '' });
      fetchData();
    } catch (err) {
      console.error('Error saving project lead:', err);
      alert('Error saving project lead: ' + err.message);
    }
  };

  const handleEditProjectLead = (person) => {
    setEditingProjectLead(person);
    setProjectLeadForm({
      name: person.name,
      linkedinUrl: person.linkedinUrl
    });
  };

  const handleDeleteProjectLead = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project lead?')) return;
    try {
      await deleteDoc(doc(db, 'projectLeads', id));
      alert('Project Lead deleted successfully!');
      fetchData();
    } catch (err) {
      console.error('Error deleting project lead:', err);
      alert('Error deleting project lead');
    }
  };

  const handleCancelProjectLeadEdit = () => {
    setEditingProjectLead(null);
    setProjectLeadForm({ name: '', linkedinUrl: '' });
  };

  if (loading) {
    return <div className="text-white text-center py-8">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-8">Manage Plan Details</h1>

        {/* Plan Information Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-semibold text-white mb-6">Plan Information</h2>

          <form onSubmit={handleSavePlanInfo} className="space-y-4">
            <div>
              <label className="block text-white mb-2">Detailed Masterplan Information</label>
              <textarea
                value={planForm.detailedInfo}
                onChange={(e) => setPlanForm({ ...planForm, detailedInfo: e.target.value })}
                className="w-full p-3 rounded-lg bg-white/20 text-white border border-white/30"
                rows="6"
                placeholder="Comprehensive masterplan details, specifications, and documentation..."
              />
            </div>

            <button
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Save Plan Information
            </button>
          </form>
        </div>

        {/* ICT Team Members Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 mt-8">
          <h2 className="text-2xl font-semibold text-white mb-6">Volunteer ICT Team</h2>

          {/* Add/Edit Form */}
          <form onSubmit={handleSaveIctMember} className="mb-8 bg-white/5 p-4 rounded-lg">
            <h3 className="text-xl text-white mb-4">
              {editingIctMember ? 'Edit ICT Team Member' : 'Add ICT Team Member'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2">Name *</label>
                <input
                  type="text"
                  value={ictMemberForm.name}
                  onChange={(e) => setIctMemberForm({ ...ictMemberForm, name: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white/20 text-white border border-white/30"
                  placeholder="Enter name..."
                  required
                />
              </div>

              <div>
                <label className="block text-white mb-2">LinkedIn URL</label>
                <input
                  type="url"
                  value={ictMemberForm.imageUrl}
                  onChange={(e) => setIctMemberForm({ ...ictMemberForm, imageUrl: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white/20 text-white border border-white/30"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
              >
                {editingIctMember ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editingIctMember ? 'Update' : 'Add'} Member
              </button>
              {editingIctMember && (
                <button
                  type="button"
                  onClick={handleCancelIctEdit}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              )}
            </div>
          </form>

          {/* ICT Team Members List */}
          <div className="flex flex-wrap gap-4">
            {ictTeamMembers.map((member) => (
              <div 
                key={member.id}
                className="group relative px-6 py-3 rounded-lg border border-white/30 text-white font-medium"
              >
                {member.imageUrl ? (
                  <a href={member.imageUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary-400">
                    {member.name}
                  </a>
                ) : (
                  member.name
                )}
                <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 p-1">
                  <button
                    onClick={() => handleEditIctMember(member)}
                    className="bg-blue-600 hover:bg-blue-700 text-white p-1 rounded"
                    title="Edit"
                  >
                    <Edit className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => handleDeleteIctMember(member.id)}
                    className="bg-red-600 hover:bg-red-700 text-white p-1 rounded"
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {ictTeamMembers.length === 0 && (
            <p className="text-gray-400 text-center py-8">No ICT team members added yet.</p>
          )}
        </div>

        {/* Masterplanners Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 mt-8">
          <h2 className="text-2xl font-semibold text-white mb-6">Masterplanners</h2>

          {/* Add/Edit Form */}
          <form onSubmit={handleSaveMasterplanner} className="mb-8 bg-white/5 p-4 rounded-lg">
            <h3 className="text-xl text-white mb-4">
              {editingMasterplanner ? 'Edit Masterplanner' : 'Add Masterplanner'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2">Name *</label>
                <input
                  type="text"
                  value={masterplannerForm.name}
                  onChange={(e) => setMasterplannerForm({ ...masterplannerForm, name: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white/20 text-white border border-white/30"
                  placeholder="Enter name..."
                  required
                />
              </div>

              <div>
                <label className="block text-white mb-2">LinkedIn URL</label>
                <input
                  type="url"
                  value={masterplannerForm.linkedinUrl}
                  onChange={(e) => setMasterplannerForm({ ...masterplannerForm, linkedinUrl: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white/20 text-white border border-white/30"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
              >
                {editingMasterplanner ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editingMasterplanner ? 'Update' : 'Add'} Masterplanner
              </button>
              {editingMasterplanner && (
                <button
                  type="button"
                  onClick={handleCancelMasterplannerEdit}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              )}
            </div>
          </form>

          {/* Masterplanners List */}
          <div className="flex flex-wrap gap-4">
            {masterplanners.map((person) => (
              <div 
                key={person.id}
                className="group relative px-6 py-3 rounded-lg border border-white/30 text-white font-medium"
              >
                {person.linkedinUrl ? (
                  <a href={person.linkedinUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary-400">
                    {person.name}
                  </a>
                ) : (
                  person.name
                )}
                <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 p-1">
                  <button
                    onClick={() => handleEditMasterplanner(person)}
                    className="bg-blue-600 hover:bg-blue-700 text-white p-1 rounded"
                    title="Edit"
                  >
                    <Edit className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => handleDeleteMasterplanner(person.id)}
                    className="bg-red-600 hover:bg-red-700 text-white p-1 rounded"
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {masterplanners.length === 0 && (
            <p className="text-gray-400 text-center py-8">No masterplanners added yet.</p>
          )}
        </div>

        {/* Project Leads Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 mt-8">
          <h2 className="text-2xl font-semibold text-white mb-6">Project Leads</h2>

          {/* Add/Edit Form */}
          <form onSubmit={handleSaveProjectLead} className="mb-8 bg-white/5 p-4 rounded-lg">
            <h3 className="text-xl text-white mb-4">
              {editingProjectLead ? 'Edit Project Lead' : 'Add Project Lead'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2">Name *</label>
                <input
                  type="text"
                  value={projectLeadForm.name}
                  onChange={(e) => setProjectLeadForm({ ...projectLeadForm, name: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white/20 text-white border border-white/30"
                  placeholder="Enter name..."
                  required
                />
              </div>

              <div>
                <label className="block text-white mb-2">LinkedIn URL</label>
                <input
                  type="url"
                  value={projectLeadForm.linkedinUrl}
                  onChange={(e) => setProjectLeadForm({ ...projectLeadForm, linkedinUrl: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white/20 text-white border border-white/30"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
              >
                {editingProjectLead ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editingProjectLead ? 'Update' : 'Add'} Project Lead
              </button>
              {editingProjectLead && (
                <button
                  type="button"
                  onClick={handleCancelProjectLeadEdit}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              )}
            </div>
          </form>

          {/* Project Leads List */}
          <div className="flex flex-wrap gap-4">
            {projectLeads.map((person) => (
              <div 
                key={person.id}
                className="group relative px-6 py-3 rounded-lg border border-white/30 text-white font-medium"
              >
                {person.linkedinUrl ? (
                  <a href={person.linkedinUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary-400">
                    {person.name}
                  </a>
                ) : (
                  person.name
                )}
                <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 p-1">
                  <button
                    onClick={() => handleEditProjectLead(person)}
                    className="bg-blue-600 hover:bg-blue-700 text-white p-1 rounded"
                    title="Edit"
                  >
                    <Edit className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => handleDeleteProjectLead(person.id)}
                    className="bg-red-600 hover:bg-red-700 text-white p-1 rounded"
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {projectLeads.length === 0 && (
            <p className="text-gray-400 text-center py-8">No project leads added yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
